// Campaign Metrics component - displays campaign performance data
// Shows cold email and nurture email campaigns with their statistics

/**
 * NOTES SUMMARY - CAMPAIGN METRICS COMPONENT
 * 
 * What this component does:
 *  - Displays campaign performance in card format
 *  - Two sections: Cold Email and Nurture Email campaigns
 *  - Shows campaign name, sent count, open rate, and reply rate
 *  - Handles loading and error states
 *  - Uses Font Awesome icons
 * 
 * Props:
 *  - campaignData: { coldEmailCampaigns: [], nurtureEmailCampaigns: [] }
 *  - loading: boolean
 *  - error: string
 */

import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faCommentDots, faBell } from '@fortawesome/free-solid-svg-icons';
import SentimentDonutChart from './SentimentDonutChart';
import { getSMSStats } from '../../utils/api';
import axios from 'axios';
import '../../styles/components.css';

const LAST_VIEWED_KEY = 'sms_replies_last_viewed';

function CampaignMetrics({ campaignData, loading, error }) {
  const [newReplyCount, setNewReplyCount] = useState(0);
  const [selectedTag, setSelectedTag] = useState('all');
  const [filteredSmsStats, setFilteredSmsStats] = useState(null);
  const [tagLoading, setTagLoading] = useState(false);

  // Check for new SMS replies on component mount
  useEffect(() => {
    checkNewReplies();
    // Check every 2 minutes for new replies
    const interval = setInterval(checkNewReplies, 120000);
    return () => clearInterval(interval);
  }, []);

  const checkNewReplies = async () => {
    try {
      // Get last viewed timestamp from localStorage
      const lastViewed = localStorage.getItem(LAST_VIEWED_KEY) || new Date(0).toISOString();

      const response = await axios.get(
        `/.netlify/functions/get-sms-reply-count?lastViewed=${lastViewed}`
      );

      if (response.data.success) {
        setNewReplyCount(response.data.data.newReplyCount);
      }
    } catch (error) {
      console.error('Failed to check new SMS replies:', error);
      // Silently fail - don't disrupt user experience
    }
  };

  const markRepliesAsViewed = () => {
    // Update localStorage with current timestamp
    localStorage.setItem(LAST_VIEWED_KEY, new Date().toISOString());
    setNewReplyCount(0);
  };

  const handleTagChange = async (tag) => {
    setSelectedTag(tag);
    if (tag === 'all') {
      setFilteredSmsStats(null);
      return;
    }
    setTagLoading(true);
    try {
      const stats = await getSMSStats(tag);
      setFilteredSmsStats(stats);
    } catch (err) {
      console.error('Failed to fetch filtered SMS stats:', err);
      setFilteredSmsStats(null);
    } finally {
      setTagLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="campaign-metrics-card">
        <div className="campaign-metrics-header">
          <FontAwesomeIcon icon={faEnvelope} className="campaign-icon" />
          <h3>Campaign Performance</h3>
        </div>
        <div className="campaign-loading">Loading campaign data...</div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="campaign-metrics-card">
        <div className="campaign-metrics-header">
          <FontAwesomeIcon icon={faEnvelope} className="campaign-icon" />
          <h3>Campaign Performance</h3>
        </div>
        <div className="campaign-error">
          Failed to load campaign data: {error}
        </div>
      </div>
    );
  }
  
  const { coldEmailCampaigns = [], nurtureEmailCampaigns = [], smsStats } = campaignData || {};
  
  return (
    <div className="campaign-metrics-card">
      <div className="campaign-metrics-header">
        <FontAwesomeIcon icon={faEnvelope} className="campaign-icon" />
        <h3>Campaign Performance</h3>
      </div>
      
      {/* Cold Email Section */}
      <div className="campaign-section">
        <h4 className="campaign-section-title">COLD EMAIL</h4>
        {coldEmailCampaigns.length > 0 ? (
          <div className="campaign-list">
            {coldEmailCampaigns.map((campaign, index) => (
              <div key={index} className="campaign-item">
                <div className="campaign-name">{campaign.name}</div>
                <div className="campaign-stats">
                  <span className="stat-sent">{campaign.sent} sent</span>
                  <span className="stat-open">{campaign.openRate}% open</span>
                  <span className="stat-click">{campaign.clickRate}% click</span>
                  <span className="stat-reply">{campaign.replyRate}% reply</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="campaign-empty">No campaigns yet - Connect your cold email platform to see performance</div>
        )}
      </div>

      {/* SMS Section */}
      <div className="campaign-section">
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
          <h4 className="campaign-section-title" style={{ margin: 0 }}>SMS (Go High Level)</h4>
          {smsStats?.availableTags?.length > 0 && (
            <select
              value={selectedTag}
              onChange={(e) => handleTagChange(e.target.value)}
              style={{
                padding: '0.25rem 0.5rem',
                fontSize: '0.8rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                background: 'white',
                color: '#374151',
                cursor: 'pointer',
                outline: 'none'
              }}
            >
              <option value="all">All Campaigns</option>
              {smsStats.availableTags.map(tag => (
                <option key={tag} value={tag}>{tag}</option>
              ))}
            </select>
          )}
          {tagLoading && (
            <span style={{ fontSize: '0.8rem', color: '#9ca3af' }}>Loading...</span>
          )}
          {newReplyCount > 0 && (
            <button
              onClick={markRepliesAsViewed}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.375rem 0.75rem',
                background: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '9999px',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => e.currentTarget.style.background = '#2563eb'}
              onMouseOut={(e) => e.currentTarget.style.background = '#3b82f6'}
              title="Click to mark as viewed"
            >
              <FontAwesomeIcon icon={faBell} />
              {newReplyCount} new {newReplyCount === 1 ? 'reply' : 'replies'}
            </button>
          )}
        </div>
        {(() => {
          const displayStats = filteredSmsStats || smsStats;
          return displayStats && displayStats.totalSent > 0 ? (
            <div className="campaign-list">
              <div className="campaign-item">
                <div className="campaign-name">
                  <FontAwesomeIcon icon={faCommentDots} style={{ marginRight: '8px', color: '#3b82f6' }} />
                  {selectedTag === 'all' ? 'SMS Outreach' : selectedTag}
                </div>
                <div className="campaign-stats">
                  <span className="stat-sent">{displayStats.totalSent} sent</span>
                  <span className="stat-reply">{displayStats.replyRate}% reply</span>
                </div>
              </div>

              {/* Appointments */}
              {(displayStats.appointments.booked > 0 || displayStats.appointments.completed > 0) && (
                <div style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#6b7280', paddingLeft: '0.5rem' }}>
                  <strong>Appointments:</strong> {displayStats.appointments.booked} booked, {displayStats.appointments.completed} completed
                  {displayStats.appointments.noShow > 0 && `, ${displayStats.appointments.noShow} no-show`}
                  {displayStats.appointments.conversionRate > 0 && (
                    <span style={{ marginLeft: '0.5rem', color: '#10b981', fontWeight: '500' }}>
                      ({displayStats.appointments.conversionRate}% completed)
                    </span>
                  )}
                </div>
              )}

              {/* Opt-outs */}
              {displayStats.optOuts > 0 && (
                <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#ef4444', paddingLeft: '0.5rem' }}>
                  <strong>Opt-outs:</strong> {displayStats.optOuts}
                </div>
              )}

              {/* Sentiment Distribution Donut Chart */}
              {displayStats.totalReplies > 0 && (
                <div style={{ marginTop: '1rem', paddingLeft: '0.5rem' }}>
                  <strong style={{ fontSize: '0.875rem', color: '#6b7280' }}>Reply Sentiment:</strong>
                  <SentimentDonutChart sentimentDistribution={displayStats.sentimentDistribution} />
                </div>
              )}
            </div>
          ) : (
            <div className="campaign-empty">
              {selectedTag !== 'all' ? `No SMS data for "${selectedTag}"` : 'No SMS data yet - Webhook will sync SMS messages in real-time'}
            </div>
          );
        })()}
      </div>

      {/* Nurture Email Section */}
      <div className="campaign-section">
        <h4 className="campaign-section-title">NURTURE EMAIL</h4>
        {nurtureEmailCampaigns.length > 0 ? (
          <div className="campaign-list">
            {nurtureEmailCampaigns.map((campaign, index) => (
              <div key={index} className="campaign-item">
                <div className="campaign-name">{campaign.name}</div>
                <div className="campaign-stats">
                  <span className="stat-sent">{campaign.sent} sent</span>
                  <span className="stat-open">{campaign.openRate}% open</span>
                  <span className="stat-click">{campaign.clickRate}% click</span>
                  <span className="stat-reply">{campaign.replyRate}% reply</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="campaign-empty">No campaigns yet - Connect your nurture email platform to see performance</div>
        )}
      </div>
    </div>
  );
}

export default CampaignMetrics;